/**
 * Task options for a time picker, grouped by phase.
 *
 * A long flat list of every task in a project is unusable — grouping by section
 * gives the same landmarks you have in Todoist, so you can jump rather than
 * scan. Completed work is excluded everywhere: you cannot start a timer on
 * something already finished, and it would double the length of the list.
 */

import type { Item, Phase } from "./types";

export interface TaskGroup {
  /**
   * The section's own id — the only value guaranteed unique.
   *
   * Callers key their <optgroup> on this. Keying on the NAME collided: every
   * task whose section is missing falls back to the same "No section" label,
   * and React then treats two groups as one and drops the options in the
   * second — so a task you were looking for simply was not in the list.
   */
  phaseId: string;
  phaseName: string;
  tasks: Item[];
}

export function groupTasksByPhase(
  items: Item[],
  phases: Phase[],
  projectId: string | undefined,
  projectOf: (item: Item) => string | undefined,
): TaskGroup[] {
  if (!projectId) return [];

  const open = items.filter((i) => projectOf(i) === projectId && i.status !== "done");
  const order = new Map(phases.map((ph, i) => [ph.id, i]));
  const nameOf = new Map(phases.map((ph) => [ph.id, ph.name]));

  const byPhase = new Map<string, Item[]>();
  for (const item of open) {
    const key = item.phase ?? "";
    const bucket = byPhase.get(key);
    if (bucket) bucket.push(item);
    else byPhase.set(key, [item]);
  }

  return [...byPhase.entries()]
    .sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999))
    .map(([phaseId, tasks]) => ({
      phaseId: phaseId || "__none",
      phaseName: nameOf.get(phaseId) ?? "No section",
      tasks: tasks.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    }));
}

/**
 * Narrow the groups to tasks matching what was typed: every word must appear
 * in the task's title or its section's name, so "sched int" finds "Schedule
 * 14 stakeholder interviews". Sections left empty are dropped.
 */
export function filterTaskGroups(groups: TaskGroup[], query: string): TaskGroup[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return groups;
  return groups
    .map((g) => ({
      ...g,
      tasks: g.tasks.filter((t) => {
        const hay = `${t.title} ${g.phaseName}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      }),
    }))
    .filter((g) => g.tasks.length > 0);
}
